import { useState } from 'react'
import { CallBackForm } from './CallBackForm'
import { EmailContactForm } from './EmailContactForm'

type Choice = 'none' | 'email' | 'call'

interface TimeoutContactBannerProps {
  onDismiss: () => void
  onSubmit?: () => void
}

export function TimeoutContactBanner({ onDismiss, onSubmit }: TimeoutContactBannerProps) {
  const [choice, setChoice] = useState<Choice>('none')
  const [done, setDone] = useState(false)

  if (done) {
    return (
      <div className='my-3 flex items-center gap-3 overflow-hidden rounded-49xl bg-white/80 px-4 py-3.5 shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34C759]'>
          <svg className='h-3.5 w-3.5 text-white' fill='currentColor' viewBox='0 0 20 20'>
            <path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
          </svg>
        </div>
        <p className='text-[15px] text-black'>Thank you. We&apos;ll be in touch soon.</p>
      </div>
    )
  }

  if (choice === 'email') {
    return (
      <EmailContactForm
        to='contact@templi.com'
        respond={() => { setDone(true); onSubmit?.(); return Promise.resolve() }}
      />
    )
  }

  if (choice === 'call') {
    return (
      <CallBackForm
        respond={() => { setDone(true); onSubmit?.(); return Promise.resolve() }}
      />
    )
  }

  return (
    <div className='overflow-hidden rounded-[22px] bg-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
      {/* Header */}
      <div className='flex items-center gap-3.5 px-5 pb-4 pt-5'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-linear-to-br from-[#32ADE6] to-[#1A7FAD] shadow-sm'>
          <span className='text-xl leading-none'>💬</span>
        </div>
        <div className='flex-1'>
          <p className='text-[17px] font-semibold leading-snug text-black'>Still waiting?</p>
          <p className='text-[13px] leading-snug text-[#6C6C70]'>
            Leave your details and we&apos;ll reach out shortly.
          </p>
        </div>
        <button
          type='button'
          onClick={onDismiss}
          className='shrink-0 text-[13px] text-[#8E8E93] hover:text-black'
        >
          Dismiss
        </button>
      </div>

      {/* Options as grouped field rows */}
      <div className='mx-4 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
        <button
          type='button'
          onClick={() => setChoice('email')}
          className='flex w-full items-center gap-3.5 px-4 py-3 text-left transition-all active:scale-[0.99] hover:bg-[rgba(120,120,128,0.08)]'
        >
          <span className='text-xl leading-none'>✉️</span>
          <div>
            <p className='text-[17px] font-medium text-black'>Contact by email</p>
            <p className='text-[13px] text-[#8E8E93]'>Leave your email and we&apos;ll reach out</p>
          </div>
          <span className='ml-auto text-[#C7C7CC]'>›</span>
        </button>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <button
          type='button'
          onClick={() => setChoice('call')}
          className='flex w-full items-center gap-3.5 px-4 py-3 text-left transition-all active:scale-[0.99] hover:bg-[rgba(120,120,128,0.08)]'
        >
          <span className='text-xl leading-none'>📞</span>
          <div>
            <p className='text-[17px] font-medium text-black'>Request a callback</p>
            <p className='text-[13px] text-[#8E8E93]'>Pick a time and we&apos;ll call you</p>
          </div>
          <span className='ml-auto text-[#C7C7CC]'>›</span>
        </button>
      </div>

      <div className='px-4 pb-5 pt-3'>
        <p className='text-center text-[12px] text-[#B0B0B5]'>
          Choose how you&apos;d like us to follow up
        </p>
      </div>
    </div>
  )
}
